import { ChatAgentCreationStep } from '../enums/ChatAgentCreationStep';
import { ChatAgentFormData } from '../interfaces/ChatAgent';
import { isAnalyticsCategoriesComplete } from './agentFormStepCompletion';
import { isWebscrapConfigValid } from './knowledgeHelper';

const isFilled = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

type StepCompletenessPredicate = (values: ChatAgentFormData) => boolean;

export const CHAT_STEP_COMPLETENESS: Record<
  ChatAgentCreationStep,
  StepCompletenessPredicate
> = {
  [ChatAgentCreationStep.BRANDING]: values => isFilled(values.size),
  // No required fields today — Form Title/Button/User Data/Disclaimer are
  // all optional; this step's Next is instead gated by dirty-state via
  // WorkflowFooter's isCurrentStepOptional/isStepDirty, not by
  // required-field completeness.
  [ChatAgentCreationStep.FORM_SETTINGS]: () => true,
  [ChatAgentCreationStep.SETTINGS]: values =>
    isFilled(values.name) &&
    isFilled(values.header_text) &&
    isFilled(values.bubble_text),
  [ChatAgentCreationStep.ANALYTICS_SETTINGS]: values =>
    isFilled(values.analytics_language) &&
    isAnalyticsCategoriesComplete(values.analytics_categories),
  // Mutually exclusive choice (AgentKnowledgeStep's `knowledge_mode`): create
  // a new knowledge base, reuse an existing one, or go without — mirrors
  // AgentLineStep's XOR pattern in the call-agent form. A knowledge base is
  // optional: opting out completes the step outright.
  [ChatAgentCreationStep.KNOWLEDGE]: values =>
    values.knowledge_mode === 'none' ||
    isFilled(values.existing_knowledge_id) ||
    (isFilled(values.knowledge_name) &&
      isFilled(values.knowledge_language) &&
      isWebscrapConfigValid(values)),
};

export const isChatStepComplete = (
  step: ChatAgentCreationStep,
  values: ChatAgentFormData
): boolean => CHAT_STEP_COMPLETENESS[step](values);

export const firstIncompleteChatStep = (
  values: ChatAgentFormData,
  stepOrder: ChatAgentCreationStep[]
): ChatAgentCreationStep | undefined =>
  stepOrder.find(step => !isChatStepComplete(step, values));
